import React, { useState } from "react";
import { SectionLayout } from "@/components/section-layout";
import { TabsContent } from "@/components/ui/tabs";
import { GenericTabBar } from "@/styles/GenericTabBar";
import { useUser } from "@/contexts/UserContext";
import { useVisibleTabs } from "@/hooks/useVisibleTabs";
import { NothingAvailable } from "@/components/NavigationUnavailable";
import type { NavKey } from "@/lib/navigation-catalog";
import { confirmLeaveIfDirty, setHasUnsavedChanges } from "@/lib/unsaved-changes";
import { AdminOnlyGuardProvider } from "./AdminOnlyGuard";
import { AccountTab } from "./tabs/AccountTab";
import { UsageTab } from "./tabs/UsageTab";
import { NotificationsTab } from "./tabs/NotificationsTab";
import { PlansTab } from "./tabs/PlansTab";
import { CompanyTab } from "./tabs/CompanyTab";
import { UsersTab } from "./tabs/UsersTab";
import { PermissionsTab } from "./tabs/PermissionsTab";

type SettingsTabKey = "account" | "notifications" | "usage" | "company" | "users" | "permissions" | "plans";

// The order of the tabs: Account, Company, AI Usage, Notifications, Users, Permissions, Plans
export const SETTINGS_TABS: { value: SettingsTabKey; label: string }[] = [
  { value: "account", label: "Account" },
  { value: "company", label: "Company" },
  { value: "usage", label: "AI Usage" },
  { value: "notifications", label: "Notifications" },
  { value: "users", label: "Users" },
  { value: "permissions", label: "Permissions" },
  { value: "plans", label: "Plans" },
];

// which tabs a role gets is the navigation matrix (nav_visibility), one element per tab
const TAB_KEYS: Record<SettingsTabKey, NavKey> = {
  account: "settings.tab.account",
  company: "settings.tab.company",
  usage: "settings.tab.usage",
  notifications: "settings.tab.notifications",
  users: "settings.tab.users",
  permissions: "settings.tab.permissions",
  plans: "settings.tab.plans",
};

export function SettingsPage() {
  // App.tsx's route guard only ever sends a user here (locked, can't
  // navigate elsewhere) when companyAddressMissing is true -- land them
  // straight on the tab that actually lets them fix it, not "Account".
  const { companyAddressMissing } = useUser();
  // only the tabs the matrix leaves ON for the role are offered, and only they are mounted
  const visible = useVisibleTabs(SETTINGS_TABS, TAB_KEYS);
  const tabs = visible.tabs;
  const [requestedTab, setActiveTab] = useState<SettingsTabKey>(companyAddressMissing ? "company" : "account");
  const activeTab = (visible.activeOf(requestedTab) ?? "account") as SettingsTabKey;

  // Radix Tabs.Content unmounts an inactive tab's contents, so switching
  // tabs silently discards an in-progress edit unless we ask first. Uses
  // the SAME app-wide flag/confirm the main sidebar's navigation checks
  // (lib/unsaved-changes.ts) -- Account/Company/Permissions each call
  // `setHasUnsavedChanges` as their own dirty state changes, so one flag
  // is checked from both "switch Settings tab" and "leave the page
  // entirely", instead of two separate mechanisms to keep in sync.
  const handleTabChange = (v: string) => {
    if (!confirmLeaveIfDirty()) return;
    setHasUnsavedChanges(false);
    setActiveTab(v as SettingsTabKey);
  };

  if (tabs.length === 0) return <SectionLayout title="Settings"><NothingAvailable message="No section of Settings is available for your account." /></SectionLayout>;

  return (
    <SectionLayout title="Settings">
      <AdminOnlyGuardProvider resetKey={activeTab}>
      <GenericTabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      >
        {tabs.some((t) => t.value === "account") && (
          <TabsContent value="account" className="mt-6">
            <AccountTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "usage") && (
          <TabsContent value="usage" className="mt-6">
            <UsageTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "notifications") && (
          <TabsContent value="notifications" className="mt-6">
            <NotificationsTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "plans") && (
          <TabsContent value="plans" className="mt-6">
            <PlansTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "company") && (
          <TabsContent value="company" className="mt-6">
            <CompanyTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "users") && (
          <TabsContent value="users" className="mt-6">
            <UsersTab />
          </TabsContent>
        )}
        {tabs.some((t) => t.value === "permissions") && (
          <TabsContent value="permissions" className="mt-6">
            <PermissionsTab />
          </TabsContent>
        )}
      </GenericTabBar>
      </AdminOnlyGuardProvider>
    </SectionLayout>
  );
}
